/* eslint-disable */
import type { Money } from "../common/currency.pb";
import type { RecurringSchedule } from "../repeatedly/common.pb";
import type { RequestContext } from "./RequestContextProto.pb";
import type { ResponseContext } from "./ResponseContextProto.pb";
import type { W9SnapshotPiiData } from "./data.pb";
import type {
  CustomOrder,
  CustomOrderConfiguration,
  Document,
  EquityDiscoveryAnimationTile,
  LearnMoreConfiguration,
  OrderSide,
  OrderType,
  StatementType,
} from "./investing/resources.pb";
import type { SyncInvestmentHolding } from "./sync_entity.pb";

export interface InitiateInvestmentOrderRequest {
  request_context?: RequestContext;
  /** Investment Entity to be bought or sold. */
  investment_entity_token?: string;
  /** Where to push/pull funds. e.g.: <token of debit card>. */
  instrument_token?: string;
  /** Token to ensure no double orders are submitted. */
  idempotence_token?: string;
  /** A decimal string containing the number of shares to be traded. */
  units?:
    | string
    | undefined;
  /**
   * Cash value of investment entity buy or sell.
   * Only one of amount or units can be set.
   */
  amount?:
    | Money
    | undefined;
  /** Order type to be submitted. */
  order_type?: OrderType;
  /** Side of order to be submitted. e.g.: BUY, SELL. */
  side?: OrderSide;
  /** The recurring schedule if this is a recurring purchase. */
  recurring_schedule?: RecurringSchedule;
  /** The custom order if this is a custom purchase. */
  custom_order?: CustomOrder;
  /** Set if this is an incentivized order. */
  investment_incentive_token?: string;
}

export interface InitiateInvestmentOrderResponse {
  /** response context */
  response_context?: ResponseContext;
  /** Identifier for order. */
  order_token?: string;
}

export interface CancelInvestmentOrderRequest {
  request_context?: RequestContext;
  /** Order to be canceled. */
  order_token?: string;
}

/** Modeled after CancelPaymentResponse. */
export interface CancelInvestmentOrderResponse {
  /** response context */
  response_context?: ResponseContext;
  status?: CancelInvestmentOrderResponseStatus;
}

/** status */
export enum CancelInvestmentOrderResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
}

export interface UpdateInvestmentHoldingRequest {
  request_context?: RequestContext;
  investment_entity_token?: string;
  action?: UpdateInvestmentHoldingRequestAction;
}

export enum UpdateInvestmentHoldingRequestAction {
  FOLLOW = "FOLLOW",
  UNFOLLOW = "UNFOLLOW",
}

export interface UpdateInvestmentHoldingResponse {
  response_context?: ResponseContext;
  /** The investment holding with the updated state. */
  investment_holding?: SyncInvestmentHolding;
}

export interface GetInvestmentStatementsRequest {
  /** The type of statement to be returned. e.g.: TAX_FORM, TRADE_CONFIRM */
  type?: StatementType;
  /**
   * Optional parameter, will return only matching statements from before
   * the timestamp specified. Default is now.
   */
  before?: number;
}

export interface GetInvestmentStatementsResponse {
  /** The list of available documents matching the statement request. */
  documents?: Document[];
}

export interface GetCustomerInvestmentSettingsRequest {
  request_context?: RequestContext;
}

export interface GetCustomerInvestmentSettingsResponse {
  /** Purchase limit in one order. */
  purchase_limit?: Money;
  /** Sell limit in one order. */
  sell_limit?: Money;
  /** This url is used by the client to link to a legal disclosure from the app. */
  equities_in_app_disclosure_url?: string;
  /**
   * Animation link shown in the discovery screen for equities. Deprecated.
   * Use equity_discovery_animation_tiles instead.
   *
   * @deprecated
   */
  equities_discovery_url?: string;
  /** Animation link shown in the discovery screen for bitcoin. */
  bitcoin_discovery_url?: string;
  /**
   * Disclosure web link.
   *
   * @deprecated
   */
  disclosures_web_url?: string;
  /** My watchlist description. */
  my_watchlist_description?: string;
  /** Context sent from server to the app. */
  response_context?: ResponseContext;
  /** This url is used by the client to link to a crypto legal disclosure from the app. */
  crypto_in_app_disclosure_url?: string;
  /** Content for My First Stock. */
  my_first_stock_configuration?: LearnMoreConfiguration;
  /** Congratulation text display on the investing home screen when customer purchase its first equity. */
  first_time_congrats_equity_text?: string;
  /** Congratulation text display on the bitcoin home screen when customer purchase its first bitcoin. */
  first_time_congrats_bitcoin_text?: string;
  /** Content for equities discovery animation. */
  equity_discovery_animation_tiles?: EquityDiscoveryAnimationTile[];
  /** Polling rate the client should use to fetch the latest portfolio, bitcoin, & entity detail charts. */
  portfolio_chart_polling_rate_ms?: number;
  bitcoin_chart_polling_rate_ms?: number;
  entity_detail_chart_polling_rate_ms?: number;
  /** Content for My First Bitcoin. */
  my_first_bitcoin_configuration?: LearnMoreConfiguration;
  /** Whether the customer is allowed to set up scheduled BTC buys. */
  scheduled_btc_buys_enabled?: boolean;
  /** Whether the customer is allowed to set up scheduled Equity buys. */
  scheduled_equity_buys_enabled?: boolean;
  /** The minimum amount to be spent on a single recurring BTC buy. */
  min_scheduled_btc_buy_amt?: Money;
  /** The minimum amount to be spent on a single recurring Equity buy. */
  min_scheduled_equity_buy_amt?: Money;
  /** The investment entity token for bitcoin. */
  bitcoin_investment_entity_token?: string;
  /** The configuration for custom orders. */
  custom_order_configuration?: CustomOrderConfiguration;
  /**
   * Congratulation title display on the investing home screen when customer purchase its first
   * equity. It is the title for "first_time_congrats_equity_text = 11".
   */
  first_time_congrats_equity_title?: string;
  /**
   * Congratulation title display on the bitcoin home screen when customer purchase its first
   * bitcoin. It is the title for "first_time_congrats_bitcoin_text = 12".
   */
  first_time_congrats_bitcoin_title?: string;
  /**
   * Congratulation title display on the stock detail screen when customer purchase its first
   * equity.
   */
  first_time_congrats_equity_detail_title?: string;
  /**
   * Congratulation text display on the stock detail screen when customer purchase its first
   * equity. Consumers of this field are to fill the placeholder `%s` with the equity's name. See
   * `SyncInvestmentEntity#display_name`.
   */
  first_time_congrats_equity_detail_text?: string;
  /** Is Trusted contact enabled for the given customer, it requires for the customer to have a brokerage account. */
  is_trusted_contact_enabled?: boolean;
}

export interface FormW9ConsentEvent {
  customer_token?: string;
  event_type?: FormW9ConsentEventEventType;
  /**
   * The following field can represent two things:
   * 1. The customer has given consent for EQUITY or BITCOIN.
   * 2. The customer's consent for EQUITY and BITCOIN have been revoked.
   */
  last_event_at?: number;
}

export enum FormW9ConsentEventEventType {
  /** CONSENT_REVOKED - Form W9 consent was revoked for both EQUITY and BITCOIN. * */
  CONSENT_REVOKED = "CONSENT_REVOKED",
  /**
   * EQUITY_CONSENT - (Deprecated; use CONSENT_GIVEN instead) Form W9 consent was given only for EQUITY. *
   *
   * @deprecated
   */
  EQUITY_CONSENT = "EQUITY_CONSENT",
  /**
   * BITCOIN_CONSENT - (Deprecated; use CONSENT_GIVEN instead) Form W9 consent was given only for BITCOIN. *
   *
   * @deprecated
   */
  BITCOIN_CONSENT = "BITCOIN_CONSENT",
  /** CONSENT_GIVEN - Form W9 consent was given for both EQUITY and BITCOIN. * */
  CONSENT_GIVEN = "CONSENT_GIVEN",
  /** TAX_INFORMATION_UPDATED - Tax information has been updated. * */
  TAX_INFORMATION_UPDATED = "TAX_INFORMATION_UPDATED",
}

export interface GetW9SnapshotPiiDataRequest {
  /** Customer token to retrieve W9SnapshotPiiData. * */
  customer_token?: string;
  /** Ask the endpoint to return latest valid and completed W9SnapshotPiiData. * */
  latest_snapshot?: boolean;
}

export interface GetW9SnapshotPiiDataResponse {
  /** W9SnapshotPiiData is required by investing to update customer's tax withholding selection. * */
  data?: W9SnapshotPiiData;
  /** Completion status of the corresponding W9 snapshot. * */
  w9_snapshot_completion_status?: GetW9SnapshotPiiDataResponseW9SnapshotCompletionStatus;
}

/** https://github.com/squareup/cash-franklin/blob/7066a857a63ee99e3075b6705e7387cec0a2dcf3/franklin/db/src/main/java/com/squareup/franklin/cryptocurrency/DbW9Snapshot.java#L89-L97 */
export enum GetW9SnapshotPiiDataResponseW9SnapshotCompletionStatus {
  VALID = "VALID",
  RETRY_EXHAUSTED = "RETRY_EXHAUSTED",
}

export interface InvestingService {
  InitiateInvestmentOrder(request: InitiateInvestmentOrderRequest): Promise<InitiateInvestmentOrderResponse>;
  CancelInvestmentOrder(request: CancelInvestmentOrderRequest): Promise<CancelInvestmentOrderResponse>;
  UpdateInvestmentHolding(request: UpdateInvestmentHoldingRequest): Promise<UpdateInvestmentHoldingResponse>;
  GetInvestmentStatements(request: GetInvestmentStatementsRequest): Promise<GetInvestmentStatementsResponse>;
  GetCustomerInvestmentSettings(
    request: GetCustomerInvestmentSettingsRequest,
  ): Promise<GetCustomerInvestmentSettingsResponse>;
  GetW9SnapshotPiiData(request: GetW9SnapshotPiiDataRequest): Promise<GetW9SnapshotPiiDataResponse>;
}
